import { Link } from "@bloomberg/media-ui-core";
import React, { ReactNode } from "react";
import styles from "~components/SectionFront/SectionFrontDisclaimer/style.module.css";
import { TranslatedMessage } from "~utils/i18n";

const CURRENCIES_TOP_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="disclaimerWebsiteIsForInfo"
      defaultMessage="Learn more about Bloomberg"
      values={{
        webUrl: (
          <Link
            className={styles.disclaimerLink}
            href="https://www.bloomberg.com/professional/products/indices/fx-findings-reference-rates/#overview"
          >
            <TranslatedMessage
              id="bloombergFixingsAndRefere_2"
              defaultMessage="Bloomberg Fixings and Reference Rates"
            />
          </Link>
        ),
      }}
    />
  </>
);

const CURRENCIES_BOTTOM_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="currencyRatesAreRepresent"
      defaultMessage="Learn more about Bloomberg"
      values={{
        webUrlDisclaimer: (
          <Link
            className={styles.disclaimerLink}
            href="https://www.bloomberg.com/notices/jp/financial-data/"
          >
            <TranslatedMessage
              id="detailsAndDisclaimer"
              defaultMessage="Bloomberg Disclaimer"
            />
          </Link>
        ),
      }}
    />
  </>
);

const CROSS_RATES_BOTTOM_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="disclaimerFxRatesMajorCurrEx"
      defaultMessage={
        "Foreign exchange rates of major world currencies. " +
        "Compare key cross rates and currency exchange rates of U.S. Dollars, Euros, British Pounds, and others."
      }
    ></TranslatedMessage>
  </>
);

const RATES_BONDS_TOP_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="disclaimerLearnAboutFixedInc"
      defaultMessage="Learn more about Bloomberg"
      values={{
        webUrl: (
          <Link
            className={styles.disclaimerLink}
            href="http://www.bloombergindices.com"
          >
            bloombergindices.com
          </Link>
        ),
      }}
    />
  </>
);

const RATES_BONDS_INDICES_TOP_DISCLAIMER = (
  <>
    <p>
      <TranslatedMessage
        id="disclaimerLearnAboutFixedInc"
        defaultMessage="Learn more about Bloomberg"
        values={{
          webUrl: (
            <Link
              className={styles.disclaimerLink}
              href="http://www.bloombergindices.com"
            >
              bloombergindices.com
            </Link>
          ),
        }}
      />
    </p>
    <p>
      <TranslatedMessage
        id="unlessIndicatedOtherwiseI"
        defaultMessage="Learn more about Bloomberg"
        values={{
          timeZone: "ET",
        }}
      />
    </p>
  </>
);

const RATES_BONDS_INDICES_BOTTOM_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="unlessIndicatedOtherwiseI"
      defaultMessage="Bloomberg Disclaimer"
    />
  </>
);

const CONSUMER_INTEREST_RATES_TOP_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="unlessIndicatedOtherwiseI"
      defaultMessage="Bloomberg Disclaimer"
      values={{
        timeZone: "ET",
      }}
    />
  </>
);

const CONSUMER_INTEREST_RATES_BOTTOM_DISCLAIMER = (
  <>
    Source:{" "}
    <Link className={styles.disclaimerLink} href="http://www.bankrate.com/blm">
      Bankrate.com
    </Link>
  </>
);

const FX_FIXINGS_BOTTOM_DISCLAIMER = (
  <>
    <TranslatedMessage
      id="disclaimerWebBfixBisl"
      defaultMessage="For general questions and information regarding BFIX email: bfixinquiry@bloomberg.net"
      values={{
        bfixEmail: (
          <Link
            className={styles.disclaimerLink}
            href="mailto:bfixinquiry@bloomberg.net"
          >
            bfixinquiry@bloomberg.net
          </Link>
        ),
        bfixMethodUrl: (
          <Link
            className={styles.disclaimerLink}
            href="https://assets.bbhub.io/professional/sites/10/BFIX-Methodology-March-2024.pdf"
          >
            <TranslatedMessage
              id="bfixMethodology"
              defaultMessage="BFIX Methodology"
            />
          </Link>
        ),
        benchmarkRegUrl: (
          <Link
            className={styles.disclaimerLink}
            href="https://www.bloomberg.com/professional/product/indices/benchmark-regulation-resources/"
          >
            <TranslatedMessage
              id="disclaimerBfixRegPageUrl"
              defaultMessage="benchmark regulation resources page"
            />
          </Link>
        ),
      }}
    />
  </>
);

type DisclaimerConfig = {
  [key: string]: {
    top: ReactNode | { [key: string]: ReactNode };
    bottom: ReactNode | { [key: string]: ReactNode };
  };
};

const DISCLAIMERS: DisclaimerConfig = {
  currencies: {
    top: CURRENCIES_TOP_DISCLAIMER,
    bottom: CURRENCIES_BOTTOM_DISCLAIMER,
  },
  "cross-rates": {
    top: CURRENCIES_TOP_DISCLAIMER,
    bottom: CROSS_RATES_BOTTOM_DISCLAIMER,
  },
  "rates-bonds": {
    top: {
      "rates-bonds": RATES_BONDS_TOP_DISCLAIMER,
      "bloomberg-fixed-income-indices": RATES_BONDS_INDICES_TOP_DISCLAIMER,
    },
    bottom: {
      "bloomberg-fixed-income-indices": RATES_BONDS_INDICES_BOTTOM_DISCLAIMER,
    },
  },
  "consumer-interest-rates": {
    top: CONSUMER_INTEREST_RATES_TOP_DISCLAIMER,
    bottom: CONSUMER_INTEREST_RATES_BOTTOM_DISCLAIMER,
  },
  "fx-fixings": {
    top: CURRENCIES_TOP_DISCLAIMER,
    bottom: FX_FIXINGS_BOTTOM_DISCLAIMER,
  },
};

export const getDisclaimerNode = (
  sectionFrontName: string,
  selectedTab: string,
  variant: "top" | "bottom"
): ReactNode | null => {
  const sectionFront = DISCLAIMERS[sectionFrontName];

  if (!sectionFront) {
    return null;
  }

  let disclaimer = sectionFront[variant];

  if (selectedTab && disclaimer && !React.isValidElement(disclaimer)) {
    disclaimer = (disclaimer as { [key: string]: ReactNode })[selectedTab];
  }

  return (disclaimer as ReactNode) || null;
};
